import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { UserRole } from '../stores/authStore';
import { emailPreferencesAPI } from '../services/api';

interface User {
  id: number;
  username: string;
  email: string;
  role: UserRole;
  isActive: boolean;
  emailVerified: boolean;
  hasViewOnlyAccess: boolean;
}

interface EmailPreferences {
  userId: number;
  username: string;
  email: string;
  usage50: boolean;
  usage75: boolean;
  usage100: boolean;
  systemNotifications: boolean;
  accountUpdates: boolean;
  powerBI: boolean;
  xero: boolean;
  dataTransfer: boolean;
  impactplan75: boolean;
  impactplan100: boolean;
  topupSuccess: boolean;
  upgradeSuccess: boolean;
}

const EmailManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [emailPreferences, setEmailPreferences] = useState<EmailPreferences[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [, setUpdatingPreferences] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await emailPreferencesAPI.getAllUsersWithPreferences();
      setUsers(response);
      
      // Extract email preferences from users data
      const preferences: EmailPreferences[] = response.map(user => ({
        userId: user.id,
        username: user.username,
        email: user.email,
        usage50: user.emailPreferences?.usage50 !== undefined ? user.emailPreferences.usage50 : false,
        usage75: user.emailPreferences?.usage75 !== undefined ? user.emailPreferences.usage75 : true,
        usage100: user.emailPreferences?.usage100 !== undefined ? user.emailPreferences.usage100 : true,
        systemNotifications: user.emailPreferences?.systemNotifications !== undefined ? user.emailPreferences.systemNotifications : true,
        accountUpdates: user.emailPreferences?.accountUpdates !== undefined ? user.emailPreferences.accountUpdates : false,
        powerBI: user.emailPreferences?.powerBI !== undefined ? user.emailPreferences.powerBI : false,
        xero: user.emailPreferences?.xero !== undefined ? user.emailPreferences.xero : false,
        dataTransfer: user.emailPreferences?.dataTransfer !== undefined ? user.emailPreferences.dataTransfer : false,
        impactplan75: user.emailPreferences?.impactplan75 !== undefined ? user.emailPreferences.impactplan75 : true,
        impactplan100: user.emailPreferences?.impactplan100 !== undefined ? user.emailPreferences.impactplan100 : true,
        topupSuccess: user.emailPreferences?.topupSuccess !== undefined ? user.emailPreferences.topupSuccess : true,
        upgradeSuccess: user.emailPreferences?.upgradeSuccess !== undefined ? user.emailPreferences.upgradeSuccess : true
      }));
      setEmailPreferences(preferences);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const handlePreferenceUpdate = async (userId: number, preference: keyof EmailPreferences, value: boolean) => {
    setUpdatingPreferences(userId);
    try {
      // Save to database first
      await emailPreferencesAPI.updateUserPreferences(userId, { [preference]: value });
      
      // Update local state immediately with the new value
      setEmailPreferences(prev => 
        prev.map(pref => 
          pref.userId === userId 
            ? { ...pref, [preference]: value }
            : pref
        )
      );
      
      toast.success('Email preference updated successfully! 📧');
      
    } catch (error: any) {
      console.error('Error updating preference:', error);
      toast.error('Failed to update email preference');
      
      // Revert the local state on error
      setEmailPreferences(prev => 
        prev.map(pref => 
          pref.userId === userId 
            ? { ...pref, [preference]: !value }
            : pref
        )
      );
    } finally {
      setUpdatingPreferences(null);
    }
  };

  const closeUserModal = () => {
    setSelectedUser(null);
    setShowUserModal(false);
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showUserModal) {
        closeUserModal();
      }
    };

    if (showUserModal) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showUserModal]);

  const refreshData = async () => {
    await fetchUsers();
  };

  // Filter users based on search and role
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const getUserPreferences = (userId: number) => {
    return emailPreferences.find(pref => pref.userId === userId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading email preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 p-8 text-white shadow-2xl">
        <div className="absolute inset-0 opacity-30 [background:radial-gradient(circle_at_15%_20%,rgba(16,185,129,0.22),transparent_45%),radial-gradient(circle_at_85%_25%,rgba(34,211,238,0.16),transparent_55%)]" />
        <div className="relative flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-emerald-300 shadow-[0_0_0_4px_rgba(52,211,153,0.14)]" />
              Notifications
              </div>
            <h1 className="text-3xl font-black tracking-tight sm:text-4xl">Email preferences</h1>
            <p className="max-w-2xl text-white/70">Configure which email notifications each user receives.</p>
            <div className="flex flex-wrap items-center gap-3 text-sm text-white/70">
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <i className="fas fa-users text-white/60"></i>
                {users.length} users
              </span>
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <i className="fas fa-filter text-white/60"></i>
                {filteredUsers.length} shown
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={refreshData}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white text-slate-900 hover:bg-slate-100 px-6 py-3 font-semibold transition-all shadow-lg"
            >
              <i className="fas fa-rotate-right"></i>
              Refresh
            </button>
            <button
              onClick={() => navigate('/users')}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/10 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:bg-white/15"
            >
              <i className="fas fa-arrow-left"></i>
              Back to users
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <input
              type="text"
              className="w-full rounded-2xl bg-white px-5 py-3 pl-12 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 transition-all"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <i className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="rounded-2xl bg-white px-4 py-3 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 transition-all font-semibold text-gray-900"
            >
              <option value="all">All Roles</option>
              <option value={UserRole.USER}>Monitor Manager</option>
              <option value={UserRole.ACCOUNT_MANAGER}>Account Manager</option>
              <option value={UserRole.CUSTOMER_SUPPORT}>Customer Support</option>
              <option value={UserRole.AUDITOR}>Auditor</option>
              <option value={UserRole.ADMIN}>Administrator</option>
              <option value={UserRole.SUPER_ADMIN}>Super Administrator</option>
            </select>
          </div>
        </div>
      </div>

      {/* User Email Preferences Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-gray-50 px-8 py-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <h3 className="text-xl font-black text-gray-900">User preferences</h3>
              {filteredUsers.length > 0 && (
                <span className="ml-3 px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm font-semibold">
                  {filteredUsers.length} users
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500">
              Configure which emails each user receives
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                  50% Usage
                </th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                  75% Usage
                </th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                  100% Usage
                </th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                  System Notifications
                </th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                  Account Updates
                </th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                  Power BI
                </th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                  Xero
                </th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                  Data Transfer
                </th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                  Impact Plan 75%
                </th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                  Impact Plan 100%
                </th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                  Topup/Upgrade Success
                </th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {filteredUsers.map((user, index) => {
                const preferences = getUserPreferences(user.id);
                return (
                  <tr key={user.id} className={`border-b border-gray-100 hover:bg-gradient-to-r hover:from-emerald-50 hover:to-green-50 transition-all duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-green-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                          {user.username.charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-semibold text-gray-900">{user.username}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                          <div className="text-xs text-gray-400 capitalize">
                            {user.role === UserRole.ACCOUNT_MANAGER ? 'Account Manager' : 
                             user.role === UserRole.CUSTOMER_SUPPORT ? 'Customer Support' :
                             user.role === UserRole.AUDITOR ? 'Auditor' :
                             user.role === UserRole.SUPER_ADMIN ? 'Super Admin' :
                             user.role === UserRole.ADMIN ? 'Administrator' : 'Monitor Manager'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={preferences?.usage50 || false}
                          onChange={(e) => handlePreferenceUpdate(user.id, 'usage50', e.target.checked)}
                          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                      </label>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={preferences?.usage75 || false}
                          onChange={(e) => handlePreferenceUpdate(user.id, 'usage75', e.target.checked)}
                          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                      </label>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={preferences?.usage100 || false}
                          onChange={(e) => handlePreferenceUpdate(user.id, 'usage100', e.target.checked)}
                          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                      </label>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={preferences?.systemNotifications || false}
                          onChange={(e) => handlePreferenceUpdate(user.id, 'systemNotifications', e.target.checked)}
                          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                      </label>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={preferences?.accountUpdates || false}
                          onChange={(e) => handlePreferenceUpdate(user.id, 'accountUpdates', e.target.checked)}
                          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                      </label>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={preferences?.powerBI || false}
                          onChange={(e) => handlePreferenceUpdate(user.id, 'powerBI', e.target.checked)}
                          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                      </label>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={preferences?.xero || false}
                          onChange={(e) => handlePreferenceUpdate(user.id, 'xero', e.target.checked)}
                          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                      </label>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={preferences?.dataTransfer || false}
                          onChange={(e) => handlePreferenceUpdate(user.id, 'dataTransfer', e.target.checked)}
                          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                      </label>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={preferences?.impactplan75 !== undefined ? preferences.impactplan75 : true}
                          onChange={(e) => handlePreferenceUpdate(user.id, 'impactplan75', e.target.checked)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                      </label>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={preferences?.impactplan100 !== undefined ? preferences.impactplan100 : true}
                          onChange={(e) => handlePreferenceUpdate(user.id, 'impactplan100', e.target.checked)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                      </label>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={(preferences?.topupSuccess !== undefined ? preferences.topupSuccess : true) && (preferences?.upgradeSuccess !== undefined ? preferences.upgradeSuccess : true)}
                          onChange={(e) => handlePreferenceUpdate(user.id, 'topupSuccess', e.target.checked)}
                          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                      </label>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleUserSelect(user)}
                        className="text-emerald-600 hover:text-emerald-800 p-2 hover:bg-emerald-50 rounded-lg transition-all duration-200"
                        title="Manage User Preferences"
                      >
                        <i className="fas fa-cog"></i>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {filteredUsers.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-green-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="fas fa-users text-3xl text-emerald-600"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">No users found</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {searchTerm ? 'No users match your search criteria.' : 'No users available to configure email preferences.'}
            </p>
          </div>
        )}
      </div>

      {/* User Preferences Modal */}
      {showUserModal && selectedUser && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeUserModal}
        >
          <div 
            className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full border border-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-black text-gray-900">
                  Email Preferences for {selectedUser.username}
                </h2>
                <button
                  onClick={closeUserModal}
                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-2 transition-all duration-200"
                  title="Close modal"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-green-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                      {selectedUser.username.charAt(0).toUpperCase()}
                    </div>
                    <div className="ml-4">
                      <div className="text-lg font-semibold text-gray-900">{selectedUser.username}</div>
                      <div className="text-sm text-gray-500">{selectedUser.email}</div>
                      <div className="text-xs text-gray-400 capitalize">
                        {selectedUser.role === UserRole.ACCOUNT_MANAGER ? 'Account Manager' : 
                         selectedUser.role === UserRole.CUSTOMER_SUPPORT ? 'Customer Support' :
                         selectedUser.role === UserRole.AUDITOR ? 'Auditor' :
                         selectedUser.role === UserRole.SUPER_ADMIN ? 'Super Admin' :
                         selectedUser.role === UserRole.ADMIN ? 'Administrator' : 'Monitor Manager'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Pool Customers Usage Notifications</h3>
                  <div className="grid grid-cols-1 gap-4">
                    {[
                      { key: 'usage50', label: '50% Usage Warning', description: 'Receive notifications when data usage reaches 50%' },
                      { key: 'usage75', label: '75% Usage Warning', description: 'Receive notifications when data usage reaches 75%' },
                      { key: 'usage100', label: '100% Usage Alert', description: 'Receive notifications when data usage reaches 100%' }
                    ].map(({ key, label, description }) => {
                      const preferences = getUserPreferences(selectedUser.id);
                      return (
                        <div key={key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                          <div>
                            <div className="font-medium text-gray-900">{label}</div>
                            <div className="text-sm text-gray-500">{description}</div>
                          </div>
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={preferences?.[key as keyof EmailPreferences] as boolean || false}
                              onChange={(e) => handlePreferenceUpdate(selectedUser.id, key as keyof EmailPreferences, e.target.checked)}
                              className="h-5 w-5 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                            />
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">System Notifications</h3>
                  <div className="grid grid-cols-1 gap-4">
                    {[
                      { key: 'systemNotifications', label: 'System Notifications', description: 'Receive general system updates and announcements' },
                      { key: 'accountUpdates', label: 'Account Updates', description: 'Receive notifications about account changes and updates' }
                    ].map(({ key, label, description }) => {
                      const preferences = getUserPreferences(selectedUser.id);
                      return (
                        <div key={key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                          <div>
                            <div className="font-medium text-gray-900">{label}</div>
                            <div className="text-sm text-gray-500">{description}</div>
                          </div>
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={preferences?.[key as keyof EmailPreferences] as boolean || false}
                              onChange={(e) => handlePreferenceUpdate(selectedUser.id, key as keyof EmailPreferences, e.target.checked)}
                              className="h-5 w-5 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                            />
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Power BI Notifications</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">Power BI</div>
                        <div className="text-sm text-gray-500">Receive all Power BI notifications (Start, Stop, Finished, Failed, Error)</div>
                      </div>
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={getUserPreferences(selectedUser.id)?.powerBI || false}
                          onChange={(e) => handlePreferenceUpdate(selectedUser.id, 'powerBI', e.target.checked)}
                          className="h-5 w-5 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                      </label>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Xero Notifications</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">Xero</div>
                        <div className="text-sm text-gray-500">Receive all Xero monitor notifications (Start, Stop, Completed, Failed, Error)</div>
                      </div>
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={getUserPreferences(selectedUser.id)?.xero || false}
                          onChange={(e) => handlePreferenceUpdate(selectedUser.id, 'xero', e.target.checked)}
                          className="h-5 w-5 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                      </label>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Data Transfer Notifications</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">Data Transfer</div>
                        <div className="text-sm text-gray-500">Receive notifications when data transfers are completed between services</div>
                      </div>
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={getUserPreferences(selectedUser.id)?.dataTransfer || false}
                          onChange={(e) => handlePreferenceUpdate(selectedUser.id, 'dataTransfer', e.target.checked)}
                          className="h-5 w-5 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                      </label>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Impact Plan Usage Notifications</h3>
                  <div className="grid grid-cols-1 gap-4">
                    {[
                      { key: 'impactplan75', label: 'Impact Plan 75% Usage Warning', description: 'Receive notifications when Impact Plan service lines reach 75% of the package limit (5000 GB)' },
                      { key: 'impactplan100', label: 'Impact Plan 100% Usage Alert', description: 'Receive notifications when Impact Plan service lines reach 100% of the package limit (5000 GB)' }
                    ].map(({ key, label, description }) => {
                      const preferences = getUserPreferences(selectedUser.id);
                      return (
                        <div key={key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                          <div>
                            <div className="font-medium text-gray-900">{label}</div>
                            <div className="text-sm text-gray-500">{description}</div>
                          </div>
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={preferences?.[key as keyof EmailPreferences] as boolean ?? true}
                              onChange={(e) => handlePreferenceUpdate(selectedUser.id, key as keyof EmailPreferences, e.target.checked)}
                              className="h-5 w-5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                            />
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Topup/Upgrade Notifications</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">Topup/Upgrade Success</div>
                        <div className="text-sm text-gray-500">Receive notifications for successful topups and package upgrades. Includes topup success emails and package update success emails.</div>
                      </div>
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={(getUserPreferences(selectedUser.id)?.topupSuccess !== undefined ? getUserPreferences(selectedUser.id)?.topupSuccess : true) && (getUserPreferences(selectedUser.id)?.upgradeSuccess !== undefined ? getUserPreferences(selectedUser.id)?.upgradeSuccess : true)}
                          onChange={(e) => handlePreferenceUpdate(selectedUser.id, 'topupSuccess', e.target.checked)}
                          className="h-5 w-5 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    onClick={closeUserModal}
                    className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-200 font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailManagement;
